import React, { createContext, useContext, useState, useEffect } from 'react';
import { applyTheme, readStoredTheme, THEME_KEY } from '../utils/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Só a escolha explícita do utilizador liga o modo escuro. Seguir a preferência
  // do sistema por omissão mudaria a aparência a quem já usa a app sem ter pedido
  // nada — o escuro fica em opt-in, nas Definições.
  const [isDarkMode, setIsDarkMode] = useState(readStoredTheme);

  useEffect(() => {
    // Aplica os três sinais que o CSS usa (ver utils/theme.js). Sem gravar: quem grava é a ação
    // (toggleTheme/setTheme), para o modo "sistema" poder ficar sem chave nenhuma.
    applyTheme(isDarkMode, { persist: false });
  }, [isDarkMode]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Só acompanha o sistema para quem escolheu "system" (que apaga a chave).
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const next = !isDarkMode;
    applyTheme(next);            // aplica e grava a escolha (uma chave só: `theme`)
    setIsDarkMode(next);
  };

  const setTheme = (theme) => {
    if (theme === 'system') {
      try { localStorage.removeItem(THEME_KEY); } catch { /* sem armazenamento */ }
      setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    } else {
      applyTheme(theme === 'dark');
      setIsDarkMode(theme === 'dark');
    }
  };

  const value = {
    isDarkMode,
    toggleTheme,
    setTheme,
    theme: isDarkMode ? 'dark' : 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
